import { createFileRoute } from "@tanstack/react-router";
import { pageHead } from "@/lib/seo";
import { PageLayout } from "@/components/site/PageLayout";
import {
  FeatureGrid,
  DeliverablesList,
  ProcessSteps,
  ServiceCta,
} from "@/components/site/ServiceBlocks";
import { FaqSection } from "@/components/site/Faq";
import { ldScript, serviceSchema, faqSchema } from "@/lib/schema";
import svcPool from "@/assets/hero-pool.jpg";
import poolProcess from "@/assets/pool-process.jpg";
import poolDeliverables from "@/assets/pool-deliverables.jpg";

const FAQS = [
  {
    q: "Do you design residential and commercial pools?",
    a: "Both. We design residential backyard pools, hotel and resort pools, community pools, and large-scale commercial aquatic facilities, each with the right regulatory package for its jurisdiction.",
  },
  {
    q: "Can you integrate solar pool heating?",
    a: "Yes. Solar thermal pool heating is one of our core disciplines. We can specify collector arrays, plumbing, and controls as part of the pool design, or as a retrofit to an existing pool.",
  },
  {
    q: "Do plans include structural and equipment layouts?",
    a: "Every pool set includes the plumbing schematic, equipment pad layout, structural details for shell and deck, and any hurricane or seismic details the local code requires.",
  },
  {
    q: "How long does a pool design take?",
    a: "Straightforward residential pool designs typically take one to two weeks. Commercial projects with variable depth, features, or bathhouses scope up front based on complexity.",
  },
];

export const Route = createFileRoute("/pools")({
  head: () => ({
    ...pageHead({
      title: "Swimming Pool Engineering Design · SunSmart Engineering",
      description:
        "Engineered pool designs for residential and commercial projects, including structural details, plumbing and solar heating, built for code-compliant permits.",
      path: "/pools",
    }),
    scripts: [
      ldScript(
        serviceSchema({
          name: "Swimming Pool Design",
          description:
            "Engineered swimming pool designs for residential and commercial projects, including structural details, plumbing, and solar heating integration.",
          url: "/pools",
          serviceType: "Pool Design",
        }),
      ),
      ldScript(faqSchema(FAQS)),
    ],
  }),
  component: PoolsPage,
});

function PoolsPage() {
  return (
    <PageLayout
      kicker="Construction Services · Swimming Pools"
      title="Swimming Pool Design Services"
      intro="We provide professional swimming pool design services tailored to meet your unique needs. From residential pools to commercial aquatic facilities, our designs focus on functionality, efficiency, and aesthetic appeal. Whether you're building a new pool or upgrading an existing one, our team ensures every detail is meticulously planned."
    >
      <ProcessSteps
        label="01"
        title={
          <>
            Areas of <span className="text-sun">expertise.</span>
          </>
        }
        image={poolProcess}
        imageAlt="Swimming pool under construction with structural rebar"
        steps={[
          { title: "Custom Pool Designs", description: "Tailored designs that complement your client's space, lifestyle, and vision, ensuring a pool that's both functional and visually stunning." },
          { title: "Energy-Efficient Systems", description: "Incorporating advanced technologies to reduce energy consumption, optimize filtration, and maintain water quality." },
          { title: "Heating Solutions", description: "Integration of solar or traditional heating systems to provide year-round comfort for residential and commercial pools." },
          { title: "Innovative Water Features", description: "Designs that include waterfalls, fountains, and other unique features to enhance your customers' pool experience." },
        ]}
      />

      <DeliverablesList
        label="02"
        title={
          <>
            Why choose SunSmart for <span className="text-sun">swimming pool design?</span>
          </>
        }
        items={[
          { title: "Expertise in Modern Pool Design", description: "We combine technical precision with creative vision to design pools that are both beautiful and efficient." },
          { title: "Sustainable Options", description: "Emphasizing eco-friendly solutions, including solar heating and energy-efficient equipment, to lower operational costs." },
          { title: "Comprehensive Support", description: "From initial concept to final plans, we work closely with you to ensure your pool meets your needs and exceeds expectations." },
          { title: "Versatility Across Projects", description: "Whether for residential backyards or large-scale commercial facilities, we adapt our expertise to suit projects of all sizes." },
        ]}
      />

      <FeatureGrid
        label="03"
        theme="surface"
        title={
          <>
            Pool design, <span className="text-sun">made easy.</span>
          </>
        }
        columns={2}
        items={[
          { title: "Simplified Construction", description: "We work closely with contractors to deliver designs that simplify the construction process while meeting all regulatory requirements." },
          { title: "Efficiency & Durability", description: "A focus on efficiency, durability, and aesthetic appeal ensures every project exceeds expectations and delivers lasting value." },
        ]}
      />

      <FaqSection items={FAQS} title={<>Pool design <span className="text-sun">questions.</span></>} />

      <ServiceCta
        label="04"
        title={
          <>
            Let's design your <span className="text-sun">next pool.</span>
          </>
        }
        intro="We understand the importance of precision and creativity in swimming pool design, from initial concept to final code-compliant plans."
        image={poolDeliverables}
        imageAlt="Pool equipment pad with pumps and filtration"
      />
    </PageLayout>
  );
}
